const env = process.env.VITE_ENV || (import.meta.env || {} || {}).VITE_ENV || 'test';
const name = process.env.PKG_NAME || import.meta.env.VITE_PKG_NAME || 'hopdrive-admin';
const version = process.env.PKG_VERSION || import.meta.env.VITE_PKG_VERSION || '5.0.0';

// Local is a first-class named environment like test/preview/prod. It points at the
// local Hasura stack the hopdrive CLI's Caddy layer serves at gql.local.hopdrive.io,
// and at sibling sites on their *.local.hopdrive.io domains (from each app's
// manifest.jsonc): admin itself, and the events-legacy service. Selected when
// VITE_ENV=local, which the `dev` script sets. Logging is on for developer visibility.
// awsDocumentsBucket stays test (S3 has no local bucket). A per-developer
// config.local.ts still overrides this as the last layer. See docs/local-dev.md.
const config_local = {
  graphqlHttpUrl: 'https://gql.local.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql.local.hopdrive.io/v1/graphql',
  graphqlClientName: 'app-admin-local',
  loggingEnableColoring: true,
  loggingEnabled: true,
  hopdriveAdminUrl: 'https://admin.local.hopdrive.io/',
  s3SignedRedirectUrl: 'https://events.local.hopdrive.io/.netlify/functions/get-s3-signed-redirect/',
  hopdriveEventsUrl: 'https://events.local.hopdrive.io/',
  awsDocumentsBucket: 'documents-test.hopdrive.com',
};

const config_demo = {
  graphqlHttpUrl: 'https://gql-test.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql-test.hopdrive.io/v1/graphql',
  graphqlClientName: 'app-admin-demo',
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin-test.hopdrive.io/',
  s3SignedRedirectUrl: 'https://events-test.hopdrive.io/.netlify/functions/get-s3-signed-redirect/',
  // Base URL for the event-handlers Netlify site. No trailing slash: callers append `/path`.
  hopdriveEventsUrl: 'https://events-test.hopdrive.io',
  awsDocumentsBucket: 'documents-test.hopdrive.com',
};

const config_test = {
  graphqlHttpUrl: 'https://gql-test.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql-test.hopdrive.io/v1/graphql',
  graphqlClientName: 'app-admin-test',
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin-test.hopdrive.io/',
  s3SignedRedirectUrl: 'https://events-test.hopdrive.io/.netlify/functions/get-s3-signed-redirect/',
  // Base URL for the event-handlers Netlify site. No trailing slash: callers append `/path`.
  hopdriveEventsUrl: 'https://events-test.hopdrive.io',
  awsDocumentsBucket: 'documents-test.hopdrive.com',
};

const config_prod = {
  graphqlHttpUrl: 'https://gql.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql.hopdrive.io/v1/graphql',
  graphqlClientName: 'app-admin-prod',
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin.hopdrive.io/',
  s3SignedRedirectUrl: 'https://events.hopdrive.io/.netlify/functions/get-s3-signed-redirect/',
  // Base URL for the event-handlers Netlify site. No trailing slash: callers append `/path`.
  hopdriveEventsUrl: 'https://events.hopdrive.io',
  awsDocumentsBucket: 'documents.hopdrive.com',
};

// Preview is a first-class named environment like test/demo/prod (mirrors
// functions/lib/config.backend.ts's gql-<pr> derivation). A preview build bakes
// VITE_ENV=preview and VITE_PR_ID; Vite inlines both, so a preview's UI talks to
// THIS PR's Hasura instead of shared gql-test. The prId feeds the dynamic constants
// below; test/prod builds never carry either.
const prId = (import.meta.env || {}).VITE_PR_ID || process.env.VITE_PR_ID || null;
const config_preview = {
  graphqlHttpUrl: `https://gql-${prId}.pr.hopdrive.io/v1/graphql`,
  graphqlWsUrl: `wss://gql-${prId}.pr.hopdrive.io/v1/graphql`,
  graphqlClientName: `app-admin-preview-${prId}`,
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin-test.hopdrive.io/',
  // Point events at THIS PR's event-handlers Netlify alias, not shared events-test.
  // Matches EVENTS_BASE_URL from infra/preview/per-pr/docker-compose.yml so the app's
  // calls and Hasura's triggers hit the same deploy. No trailing slash: callers append `/path`.
  s3SignedRedirectUrl: `https://event-handlers-legacy-${prId}.pr.hopdrive.io/.netlify/functions/get-s3-signed-redirect/`,
  hopdriveEventsUrl: `https://event-handlers-legacy-${prId}.pr.hopdrive.io`,
  awsDocumentsBucket: 'documents-test.hopdrive.com',
};

const config = {
  appEnv: env,
  // Baked on preview builds only; the build-identity footer shows it as `preview <prId>`.
  prId,
  name: name,
  version: version,
  fullName: `${name}-v${version}`,
  versionCheckFrequency: 3600000,

  // Firebase claims
  firebaseClaimsNamespace: 'https://hasura.io/jwt/claims',
  firebaseClaimsAllowedRegions: 'x-hasura-allowed-regions',
  firebaseClaimsAllowedRoles: 'x-hasura-allowed-roles',
  firebaseClaimsDefaultRole: 'x-hasura-default-role',
  firebaseClaimsAllowedCustomers: 'x-hasura-allowed-customers',
  firebaseClaimsAllowedPayers: 'x-hasura-allowed-payers',
  firebaseClaimsPayerChildCustomers: 'x-hasura-payer-child-customers',
  firebaseClaimsCustomerId: 'x-hasura-customer-id',
  firebaseClaimsUserId: 'x-hasura-user-id',
  firebaseClaimsDriverId: 'x-hasura-driver-id',
  firebaseClaimsUserEmail: 'x-hasura-user-email',

  // Google config
  googleClientUrl: 'https://clients3.google.com/generate_204',

  // Sentry config
  sentry: {
    url: 'https://5186cd19f98e4077a5c9ed9ebea39b8c@o491290.ingest.sentry.io/6751078',
  },

  // Email config
  emailInfo: 'info@hopdrive.com',
  emailSupport: 'support@hopdrive.com',
  // Environment specific keys
  ...config_test, //opinionated default to test
  ...(env === 'local' ? config_local : null),
  ...(env === 'demo' ? config_demo : null),
  ...(env === 'test' ? config_test : null),
  // preview without a baked prId can't guess its PR; the test default above holds.
  ...(env === 'preview' && prId ? config_preview : null),
  ...(env === 'prod' ? config_prod : null),
};

export default config;
